import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { E3Button } from "e3-components";
import styles from "./E3ModalPopup.module.css";
import "@grapecity/wijmo.styles/wijmo.css";
import { Popup } from "@grapecity/wijmo.react.input";
import { getLocalStorageItem } from "e3-common/utils/localStorageUtil";
import { LOCAL_STORAGE_KEYS } from "e3-common/constants/enumerates";
import { PropConst } from "e3-common/constants/common";
/**
 * E3ModalPopup
 * @type React.FC<E3ModalPopupPropTypes>
 */
const E3ModalPopup = React.forwardRef((props, ref) => {
  const findBtnStyle = JSON.parse(
    getLocalStorageItem(LOCAL_STORAGE_KEYS.BUTTON_STYLE)
  );
  const refContainer = useRef();
  const confirmBtnRef = useRef(E3Button);
  const [currState, setCurrState] = useState({
    // isOpen: false,
    dialog: null,
    title: props.defaultTitle,
    width: props.defaultWidth,
    height: props.defaultHeight,
    showFooter: props.showFooter ?? true,
    left: props.left ?? 0,
    top: props.top ?? 0,
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    showPopup: () => _showPopup(),
    closePopup: _closePopup,
    setTitle(title) {
      setCurrState((prev) => ({ ...prev, title: title }));
    },
    setWidth(width) {
      setCurrState((prev) => ({ ...prev, width: width }));
    },
    setHeight(height) {
      setCurrState((prev) => ({ ...prev, height: height }));
    },
    popConfirmEnable(value = true) {
      if (value) {
        confirmBtnRef.current?.enable();
      } else {
        confirmBtnRef.current?.disable();
      }
    },
    setShowFooter(value = true) {
      setCurrState((prev) => ({ ...prev, showFooter: value }));
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "E3ModalPopup: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3ModalPopupPropTypes).includes(p)) {
      console.error(`E3ModalPopup: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /***********************************************************************************************
   *
   * Popup 이벤트 영역
   *
   ***********************************************************************************************/
  const _popupInitialized = (popup) => {
    /**
     * when the user presses the ESC key in the modal, this event will be canceled
     */
    popup?.hostElement?.addEventListener(
      "keydown",
      (e) => {
        if (e?.key === "Escape") {
          e.preventDefault();
        }
      },
      true
    );
    popup.owner = refContainer;
    setCurrState((prev) => ({ ...prev, dialog: popup }));

    if (props.initialized) {
      props.initialized(popup);
    }
  };

  const _showPopup = () => {
    currState.dialog?.show(true);
    props.initEvent && props.initEvent();
  };

  const _closePopup = () => {
    currState.dialog?.hide(true);
    props.endEvent && props.endEvent();
  };

  const _showing = (popup) => {
    if (props.showing) {
      props.showing();
    }
  };

  const _shown = (popup) => {
    console.log("_shown");
    if (props.backDropBg) popup._bkDrop.style.background = "none";
    wijmo.toggleClass(popup.hostElement, "custom-animation-visible", true);

    if (props.shown) {
      props.shown(popup);
    }
  };
  const _hiding = (popup) => {
    console.log("_hiding");
    wijmo.toggleClass(popup.hostElement, "custom-animation-visible", false);

    if (props.hiding) {
      props.hiding(popup);
    }
  };

  const _hidden = (popup) => {
    console.log("_hidden");
    if (props.hidden) {
      props.hidden(popup);
    }
  };

  return (
    <div ref={refContainer}>
      <Popup
        className={`custom-animation ${props.className}`}
        hideTrigger={props.hideTrigger}
        isDraggable={props.isDraggable}
        isResizable={props.isResizable}
        initialized={_popupInitialized}
        showing={_showing}
        shown={_shown}
        hiding={_hiding}
        hidden={_hidden}
        style={{
          width: currState.width,
          height: currState.height,
        }}
      >
        <div className={styles.modalContainer}>
          {props.showHeader ? (
            props.headerBtns ? (
              <div className={`modal-header ${styles.modalHeader}`}>
                <h3>{currState.title}</h3>
                <E3Button
                  label={props.headerBtns.deleteButtonLabel}
                  buttonType={props.closeBtnOption?.buttonType}
                  iconType={
                    props.headerBtns?.iconType ??
                    findBtnStyle?.btnClose?.iconType
                  }
                  colorType={
                    props.headerBtns?.colorType ??
                    findBtnStyle?.btnClose?.colorType
                  }
                  onClick={() => {
                    props.headerBtns?.delBtnEvent() ?? props.closeBtnEvent();
                    _closePopup();
                  }}
                  checkAuth={false}
                />
              </div>
            ) : (
              <div className={`modal-header ${styles.modalHeader}`}>
                <h3>{currState.title}</h3>
                {props.headerBtns}
              </div>
            )
          ) : null}
          <div
            className={styles.modalBody}
            style={{
              padding: props?.padding ?? "10px",
              ...(props.overflowIsUnSet && { overflow: "unset" }),
            }}
          >
            {props.children}
          </div>
          {currState.showFooter ? (
            <div className={styles.modalFooter}>
              {props.leftButtons}
              {props.confirmBtnEvent || props.showConfirmBtn ? (
                <E3Button
                  ref={confirmBtnRef}
                  label={props.confirmBtnLabel}
                  authBtn={PropConst.authBtn.CONFIRM}
                  checkAuth={false}
                  onClick={() => {
                    props.confirmBtnEvent && props.confirmBtnEvent();
                  }}
                  buttonType={props.confirmBtnOption?.buttonType}
                  iconType={
                    props.confirmBtnOption?.iconType ??
                    findBtnStyle?.btnConfirmW?.iconType
                  }
                  colorType={
                    props.confirmBtnOption?.colorType ??
                    findBtnStyle?.btnConfirmW?.colorType
                  }
                />
              ) : null}
              {props.closeBtnEvent || props.showCloseBtn ? (
                <E3Button
                  label={props.closeBtnLabel}
                  authBtn={"CLOSE"}
                  onClick={() => {
                    props.closeBtnEvent && props.closeBtnEvent();
                    _closePopup();
                  }}
                  buttonType={props.closeBtnOption?.buttonType}
                  iconType={
                    props.closeBtnOption?.iconType ??
                    findBtnStyle?.btnClose?.iconType
                  }
                  colorType={
                    props.closeBtnOption?.colorType ??
                    findBtnStyle?.btnClose?.colorType
                  }
                />
              ) : null}

              {props.rightButtons}
            </div>
          ) : null}
        </div>
      </Popup>
    </div>
  );
});

const E3ModalPopupPropTypes = {
  /* custom props */
  defaultTitle: PropTypes.string,
  showFooter: PropTypes.bool,
  showHeader: PropTypes.bool,
  headerBtns: PropTypes.object,
  leftButtons: PropTypes.node,
  confirmBtnLabel: PropTypes.string,
  confirmBtnOption: PropTypes.object,
  closeBtnLabel: PropTypes.string,
  closeBtnOption: PropTypes.object,
  rightButtons: PropTypes.node,
  defaultHeight: PropTypes.string,
  defaultWidth: PropTypes.string,
  backDropBg: PropTypes.bool,
  showConfirmBtn: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  padding: PropTypes.string,

  /* custom event */
  confirmBtnEvent: PropTypes.func,
  closeBtnEvent: PropTypes.func,
  initEvent: PropTypes.func,
  endEvent: PropTypes.func,

  /* Popup props */
  children: PropTypes.node,
  className: PropTypes.string,
  isDraggable: PropTypes.bool,
  isResizable: PropTypes.bool,
  hideTrigger: PropTypes.bool,
  overflowIsUnSet: PropTypes.bool,

  /* Popup event */
  initialized: PropTypes.func,
  showing: PropTypes.func,
  shown: PropTypes.func,
  hiding: PropTypes.func,
  hidden: PropTypes.func,
};

E3ModalPopup.propTypes = E3ModalPopupPropTypes;
E3ModalPopup.defaultProps = {
  showConfirmBtn: false,
  showCloseBtn: false,
  isDraggable: true,
  isResizable: true,
  hideTrigger: false,
  defaultTitle: "MODAL",
  showFooter: true,
  showHeader: true,
  headerBtns: false,
  confirmBtnLabel: "확인",
  confirmBtnOption: {},
  closeBtnLabel: "닫기",
  closeBtnOption: {},
  defaultHeight: "500px",
  defaultWidth: "500px",
  backDropBg: false,
  className: null,
  overflowIsUnSet: false,
  padding: "10px",
};
E3ModalPopup.displayName = "E3ModalPopup";
export default E3ModalPopup;
